import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFlowOwner } from "@/components/FlowAccessGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TRANSACTION_COUNTRIES } from "@/lib/countries";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentMethodSelector } from "@/components/ui/payment-method-selector";
import { PricingTable } from "@/components/ui/pricing-table";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getGeoData } from "@/utils/geolocation";
import { trackRedditEvent } from "@/lib/reddit-pixel";
import { toast } from "@/hooks/use-toast";

type PricingTier = {
  name: string;
  price: string;
  pricePeriod: string;
  features: string[];
};

type Flow = {
  id: string;
  business_name: string;
  product_name: string;
  flow_type: string;
  price: number | null;
  price_period: string | null;
  logo_url: string | null;
  product_image_url: string | null;
  pricing_tiers?: PricingTier[];
  discount_price?: number | null;
  collect_email?: boolean;
  email_required?: boolean;
  collect_name?: boolean;
  name_required?: boolean;
};

const PaymentFlow = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useFlowOwner();

  const [isLoading, setIsLoading] = useState(true);
  const [flow, setFlow] = useState<Flow | null>(null);
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  
  const [paymentMethod] = useState<"card">("card");
  const [email, setEmail] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvc, setCvc] = useState("");
  const [name, setName] = useState("");
  const [saveInfo, setSaveInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!flowId) return;
        
        const { data, error } = await supabase
          .from('flows')
          .select('*')
          .eq('id', flowId)
          .single();
        
        if (error) {
          throw error;
        }
        
        setFlow(data as Flow);

        // Get selected tier from session storage if it exists.
        // Key is scoped by flowId so a tier picked on one pricing-table flow
        // can't leak into a different flow's payment page.
        const storedTier = sessionStorage.getItem(`selectedPricingTier:${flowId}`);
        if (storedTier) {
          setSelectedTier(JSON.parse(storedTier));
        }

        // If this is a pricing_table flow and we landed on /payment/:flowId
        // WITHOUT a selected tier (e.g. user opened the payment link directly
        // instead of going through the pricing table), redirect them to the
        // pricing table so they can pick a plan — otherwise they'd see the
        // stale default $9.99 fallback.
        if (
          data?.flow_type === 'pricing_table' &&
          !storedTier &&
          window.location.pathname.includes('/payment/')
        ) {
          navigate(`/pricing-table/${flowId}`, { replace: true });
          return;
        }
        
        // Record page view analytics
        recordFlowVisit();
      } catch (error) {
        console.error('Error fetching flow:', error);
        // Handle error - show notification or redirect
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlow();
  }, [flowId]);
  
  // Function to get visitor's country from IP address
  const getCountryFromIP = async () => {
    try {
      // Use the free IP geolocation API to get country information
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return data.country_name || 'Unknown';
    } catch (error) {
      return 'Unknown';
    }
  };
  
  // Function to record a flow visit
  const recordFlowVisit = async () => {
    if (!flowId) return;
    
    try {
      // Check if flow is embedded or directly visited
      const isEmbedded = window.self !== window.top;
      
      // Skip recording views for embedded flows since the SessionTracker already handles that
      if (isEmbedded) {
        return;
      }
      
      // Generate a unique key for this flow view
      const viewKey = `flow_view_${flowId}`;
      
      // Check if this user has already viewed this flow today
      const viewDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format
      const storedView = localStorage.getItem(viewKey);
      
      // If user already viewed this flow today, don't record again
      if (storedView === viewDate) {
        return;
      }
      
      // Store the view in localStorage to prevent duplicates
      localStorage.setItem(viewKey, viewDate);
      
      // Generate or retrieve a unique user identifier
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }

      // Get referrer information
      const referrer = document.referrer || 'direct';
      const referrerDomain = referrer !== 'direct' ? 
        new URL(referrer).hostname.replace('www.', '') : 'direct';
      
      // Get country information
      const geoData = await getGeoData();
      
      // Insert the analytics data with the current date
      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        event_type: 'view',
        referrer: referrerDomain,
        is_embedded: isEmbedded,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: geoData?.country || 'Unknown',
        user_identifier: userIdentifier,
        view_date: viewDate
      });
    } catch (error) {
      console.error('Failed to record view analytics:', error);
      // Silent fail - don't interrupt user experience
    }
  };

  // Track if conversion has already been recorded to prevent duplicates
  const [conversionRecorded, setConversionRecorded] = useState(false);

  const formatCardNumber = (value: string) => {
    // If user starts typing, simulate successful payment and redirect to thank you page
    if (value.length > 0) {
      handleSimulatedPayment();
      return "";
    }
    return value;
  };

  // Compute the actual amount this conversion is worth, used by analytics
  // revenue rollups. Pricing-table flows must use the selected tier (not the
  // base flow.price), discount flows use the discounted price, pre-order flows
  // collect intent without payment so amount is null. Email-only flow types
  // never reach this component.
  const conversionAmount = (): number | null => {
    if (flow?.flow_type === "preorder") return null;
    if (selectedTier) return parseFloat(selectedTier.price) || null;
    if (flow?.flow_type === "discount" && flow.discount_price != null) return flow.discount_price;
    return flow?.price ?? null;
  };

  const handleSimulatedPayment = async () => {
    // Prevent duplicate conversion recording
    if (conversionRecorded || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setConversionRecorded(true);

    if (flowId) {
      try {
        // Store selected tier in session storage for thank you page
        if (selectedTier) {
          sessionStorage.setItem(`selectedPricingTier:${flowId}`, JSON.stringify(selectedTier));
        }

        const amount = conversionAmount();

        // For embedded flows, dispatch a custom event for SessionTracker to record the conversion
        const isEmbedded = window.self !== window.top;
        if (isEmbedded) {
          // Dispatch a custom event that SessionTracker will listen for
          const conversionEvent = new CustomEvent('wtc-conversion', {
            detail: { email: email || undefined, name: name || undefined, amount }
          });
          window.dispatchEvent(conversionEvent);
        } else {
          // For non-embedded flows, record conversion directly
          const geoData = await getGeoData();
          let userIdentifier = localStorage.getItem('flow_user_id');
          if (!userIdentifier) {
            userIdentifier = crypto.randomUUID();
            localStorage.setItem('flow_user_id', userIdentifier);
          }
          const viewDate = new Date().toISOString().split('T')[0];

          const insertRow: Record<string, unknown> = {
            flow_id: flowId,
            email: email || null,
            name: name || null,
            event_type: 'conversion',
            referrer: document.referrer || 'direct',
            is_embedded: false,
            user_agent: navigator.userAgent,
            path: window.location.pathname,
            country: geoData?.country || 'Unknown',
            user_identifier: userIdentifier,
            view_date: viewDate,
            amount,
          };

          let { error: insertError } = await supabase.from('flow_analytics').insert(insertRow);

          // Production may not yet have the amount column — retry without it
          // rather than silently dropping the conversion row.
          if (insertError && /column .* (does not exist|of relation)/i.test(insertError.message || '')) {
            const msg = insertError.message || '';
            const stripped = { ...insertRow };
            for (const col of ['amount', 'name']) {
              if (msg.includes(col)) delete stripped[col];
            }
            ({ error: insertError } = await supabase.from('flow_analytics').insert(stripped));
          }

          if (insertError) {
            // Don't navigate to thank-you if the conversion row never landed —
            // the visitor would walk away thinking it worked while the owner's
            // analytics shows nothing. Surface it and let them retry.
            throw insertError;
          }
        }

        // Track lead with Reddit Pixel + CAPI
        trackRedditEvent('Lead', { email: email || undefined });

        // Navigate to thank you page
        navigate(`/thank-you/${flowId}`);
      } catch (error) {
        console.error('Payment error:', error);
        setIsSubmitting(false);
        setConversionRecorded(false); // Reset on error to allow retry
        toast({
          title: "Couldn't record submission",
          description: "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      navigate(-1);
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Flow not found</h1>
          <p className="text-muted-foreground">The payment flow you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // If it's a pricing table flow, show only the pricing table
  if (flow.flow_type === 'pricing_table' && window.location.pathname.includes('/pricing-table/')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl">
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              <PricingTable
                pricingTiers={flow.pricing_tiers || [
                  {
                    name: "Basic",
                    price: flow.price?.toString() || "9.99",
                    pricePeriod: flow.price_period || "month",
                    features: ["Feature 1", "Feature 2", "Feature 3"]
                  }
                ]}
                productName={flow.product_name}
                titleStyle={(() => {
                  const d: any = (flow as any).pricing_table_design;
                  if (!d) return undefined;
                  return {
                    text: d.titleText,
                    fontFamily: d.titleFontFamily,
                    fontWeight: d.titleFontWeight,
                    fontStyle: d.titleFontStyle,
                    fontSize: d.titleFontSize,
                    align: d.titleAlign,
                    transform: d.titleTransform,
                    color: d.titleColor,
                    accentColor: d.primaryColor,
                  };
                })()}
                cardTypography={(() => {
                  const d: any = (flow as any).pricing_table_design;
                  if (!d) return undefined;
                  return {
                    tierNameFontFamily: d.tierNameFontFamily,
                    tierNameFontWeight: d.tierNameFontWeight,
                    tierNameFontStyle: d.tierNameFontStyle,
                    tierNameTransform: d.tierNameTransform,
                    priceFontFamily: d.priceFontFamily,
                    priceFontWeight: d.priceFontWeight,
                    priceFontStyle: d.priceFontStyle,
                    featureFontFamily: d.featureFontFamily,
                    featureFontWeight: d.featureFontWeight,
                    featureFontStyle: d.featureFontStyle,
                    buttonFontFamily: d.buttonFontFamily,
                    buttonFontWeight: d.buttonFontWeight,
                    buttonTransform: d.buttonTransform,
                  };
                })()}
                onSelectPlan={(tier) => {
                  try {
                    trackRedditEvent('AddToCart');
                    sessionStorage.setItem(`selectedPricingTier:${flowId}`, JSON.stringify(tier));
                    // The route changes but `flowId` doesn't, so the fetchFlow effect
                    // won't re-run and won't pick the tier up from sessionStorage.
                    // Set it synchronously here so the payment view renders the right plan.
                    setSelectedTier(tier);
                    navigate(`/payment/${flowId}`);
                  } catch (error) {
                    console.error('Error in onSelectPlan:', error);
                  }
                }}
                isSubmitting={isSubmitting}
                isEmbedded={window.self !== window.top}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // For non-pricing table flows, show the full payment flow UI
  const formatPrice = () => {
    if (selectedTier) {
      return `$${selectedTier.price}/${selectedTier.pricePeriod === "month" ? "mo" : "yr"}`;
    }
    
    if (!flow?.price) return "Free";
    
    if (flow.flow_type === "subscription") {
      return `$${flow.price}/${flow.price_period === "month" ? "mo" : "yr"}`;
    }
    
    if (flow.flow_type === "preorder") {
      return `$${flow.price}`;
    }
    
    if (flow.flow_type === "discount" && flow.discount_price) {
      return `$${flow.discount_price}`;
    }
    
    return `$${flow.price}`;
  };

  const formatDescription = () => {
    if (selectedTier) {
      return `${selectedTier.name} - ${selectedTier.pricePeriod === "month" ? "Monthly" : "Annual"} subscription`;
    }
    
    if (flow?.flow_type === "subscription") {
      return `${flow.price_period === "month" ? "Monthly" : "Annual"} subscription`;
    }
    
    if (flow?.flow_type === "preorder") {
      return "Pre-order - no payment required now";
    }
    
    if (flow?.flow_type === "discount") {
      return "Special discounted price";
    }
    
    return "One-time payment";
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left Column - Product Info */}
          <div className="w-full md:w-5/12 bg-gray-900 text-white p-8 rounded-lg">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center text-gray-300 hover:text-white mb-8"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </button>
            
            <div className="space-y-8">
              {/* Product Logo and Name */}
              <div className="flex items-center gap-4">
                <div className="bg-gray-800 p-2 rounded-lg">
                  {flow.logo_url ? (
                    <img 
                      src={flow.logo_url} 
                      alt={flow.business_name} 
                      className="w-10 h-10 object-contain" 
                    />
                  ) : (
                    <div className="w-10 h-10 bg-yellow-500 rounded flex items-center justify-center text-lg font-bold">
                      {flow.business_name ? flow.business_name.charAt(0) : flow.product_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{flow.product_name}</h2>
                  <p className="text-sm text-gray-300">{flow.business_name}</p>
                  {selectedTier && (
                    <p className="text-sm text-amber-400 mt-1">{selectedTier.name} Plan</p>
                  )}
                </div>
              </div>
              
              {/* Product Image */}
              {flow.product_image_url && (
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={flow.product_image_url} 
                    alt={flow.product_name}
                    className="w-full h-48 object-cover" 
                  />
                </div>
              )}
              
              {/* Selected Plan Features */}
              {selectedTier && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Plan Features</h3>
                  <ul className="space-y-2">
                    {selectedTier.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <svg className="h-4 w-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Summary */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span>{selectedTier ? selectedTier.name : "Total"}</span>
                    {flow.flow_type === "discount" && flow.price && flow.discount_price ? (
                      <div className="text-right">
                        <span className="text-gray-400 line-through mr-2">${flow.price}</span>
                        <span className="text-xl font-bold text-green-400">${flow.discount_price}</span>
                      </div>
                    ) : (
                      <span className="text-xl font-bold">{formatPrice()}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">
                    {formatDescription()}
                  </p>
                  {flow.flow_type === "discount" && flow.price && flow.discount_price && (
                    <p className="text-sm text-green-400">
                      You save ${(flow.price - flow.discount_price).toFixed(2)} ({Math.round((1 - flow.discount_price / flow.price) * 100)}% off)
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Payment Form */}
          <div className="w-full md:w-7/12">
            <Card className="shadow-lg border-0">
              <CardContent className="p-8">
                <h2 className="text-xl font-bold mb-6">
                  {flow?.flow_type === "preorder" ? "Reserve your spot" : "Payment"}
                </h2>
                <div className="space-y-6">
                  {flow?.flow_type !== "preorder" && <PaymentMethodSelector />}

                  {paymentMethod === "card" && (
                    <form onSubmit={(e) => { e.preventDefault(); handleSimulatedPayment(); }} className="space-y-4 mt-6">
                      {/* Email field - shown if collect_email is true (default) or undefined (backwards compatibility) */}
                      {(flow?.collect_email !== false) && (
                        <div className="space-y-2">
                          <Label htmlFor="email">
                            Email
                            {flow?.email_required !== false && <span className="text-red-500 ml-1">*</span>}
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="email@example.com"
                            required={flow?.email_required !== false}
                          />
                        </div>
                      )}

                      {/* Name field - only shown if collect_name is explicitly true */}
                      {flow?.collect_name && (
                        <div className="space-y-2">
                          <Label htmlFor="name">
                            Name
                            {flow?.name_required && <span className="text-red-500 ml-1">*</span>}
                          </Label>
                          <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full name"
                            required={flow?.name_required}
                          />
                        </div>
                      )}

                      {/* Card / billing fields — hidden for pre-order flows
                          where the message is "no payment now". The form still
                          collects email (and optional name) above, so a submit
                          records the intent without asking for a card. */}
                      {flow?.flow_type !== "preorder" && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="card-number">Card information</Label>
                            <Input
                              id="card-number"
                              value={cardNumber}
                              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                              placeholder="1234 1234 1234 1234"
                              className="rounded-b-none"
                            />
                            <div className="flex">
                              <Input
                                id="expiry"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                placeholder="MM / YY"
                                className="rounded-t-none rounded-r-none border-t-0 border-r-0"
                              />
                              <Input
                                id="cvc"
                                value={cvc}
                                onChange={(e) => setCvc(e.target.value)}
                                placeholder="CVC"
                                className="rounded-t-none rounded-l-none border-t-0 border-l-0"
                              />
                            </div>
                          </div>

                          {/* Name on card - only shown if NOT collecting name separately */}
                          {!flow?.collect_name && (
                            <div className="space-y-2">
                              <Label htmlFor="card-name">Name on card</Label>
                              <Input
                                id="card-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Full name"
                              />
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label htmlFor="country">Country or region</Label>
                            <Select defaultValue="US">
                              <SelectTrigger>
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent>
                                {TRANSACTION_COUNTRIES.map((c) => (
                                  <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="save-info"
                              checked={saveInfo}
                              onCheckedChange={(checked) => setSaveInfo(checked as boolean)}
                            />
                            <label
                              htmlFor="save-info"
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Save my information for faster checkout
                            </label>
                          </div>
                        </>
                      )}

                      <Button
                        type="submit"
                        className="w-full bg-amber-400 hover:bg-amber-500 text-black font-semibold h-12 mt-2"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Processing..." :
                          (flow?.flow_type === "preorder")
                            ? ((flow as any)?.preorder_cta_text || `Pre-order for ${formatPrice()}`)
                            : flow?.flow_type === "discount"
                              ? `Pay discounted price ${formatPrice()}`
                              : `Pay ${formatPrice()}`
                        }
                      </Button>

                      {(flow?.flow_type === "preorder") && (
                        <div className="mt-4">
                          <Alert>
                            <AlertDescription>
                              {(flow as any)?.preorder_message || "By pre-ordering, you'll be among the first to receive the product when it launches. No payment is required at this time."}
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}

                      {flow?.flow_type === "discount" && flow.price && flow.discount_price && (
                        <div className="mt-4">
                          <Alert className="bg-green-50 border-green-200">
                            <AlertDescription className="text-green-800">
                              Limited time offer! You're saving ${(flow.price - flow.discount_price).toFixed(2)} ({Math.round((1 - flow.discount_price / flow.price) * 100)}% off) on this purchase.
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}
                    </form>
                  )}
                </div>
              </CardContent>
              
              {!isPaid && (
                <div className="px-8 pb-6">
                  <p className="text-xs text-gray-500">
                    Powered by <a href="https://willtheyconvert.com/" className="text-gray-600 hover:text-gray-900 underline">willtheyconvert.com</a>
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFlow; 